/** R-16 T-127/T-128: Ask is a page at /ask; every launcher navigates there, labelled "Ask <platform name>" from meta. */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircleQuestion } from 'lucide-react';
import { useMeta } from '@/app/meta';
import { Button, type ButtonProps } from '@/kit';

export const ASK_ROUTE = '/ask';

/** "Ask <platform name>" — the platform's display name from GET /api/meta, never a literal. */
export function useAskLabel(): string {
  // R-16 T-134: the platform's short name ("Ask CORE+"); the platform name, then the app name, when it is unset
  const { platformShortName, platformName, appName } = useMeta();
  return `Ask ${platformShortName?.trim() || platformName?.trim() || appName}`;
}

export interface AskLauncher {
  /** Open the Ask page, optionally with a question to start from (still editable before sending). */
  launch: (question?: string) => void;
}

export function useAskLauncher(): AskLauncher {
  const navigate = useNavigate();
  const launch = useCallback((question?: string) => navigate(question ? `${ASK_ROUTE}?q=${encodeURIComponent(question)}` : ASK_ROUTE), [navigate]);
  return { launch };
}

export function AskLauncherButton({ onClick, children, ...props }: Omit<ButtonProps, 'onClick'> & { onClick: () => void }) {
  const label = useAskLabel();
  return (
    <Button onClick={onClick} {...props}>
      <MessageCircleQuestion size={14} aria-hidden />
      {children ?? label}
    </Button>
  );
}
